// 오디오 반응형 오브 — delight-voice-orb(WebGL2 셰이더 + 듀얼 채널 오디오 엔진) 포팅.
// 사운드 이펙트는 전부 제외하고, AnalyserNode를 props로 주입받는 순수 시각화 컴포넌트로
// 재구성했다. 출력(에이전트 음성)은 파형·링·흐름 가속을, 입력(마이크)은 수축 호흡을 만든다.
//
// 2026-08 QA: orb-demo-vert.vercel.app 랩의 모션 프리셋 엔진으로 업데이트.
// 프로덕션 모션은 'Pulse' (디자인 확정 — QA 스크린샷 3장 모두 Pulse 선택 상태).
// "정지에 가까운 휴지 상태 — 발화가 시작되는 순간 살아난다"는 대비형 프리셋.
// 셰이더는 랩 원본 전체를 그대로 포팅하고(모드 분기 포함), PULSE 상수로 고정한다.

export type OrbState = 'idle' | 'connecting' | 'connected' | 'speaking' | 'ended';

// ---------- 셰이더 (랩 원본 + 크로스페이드 텍스처 유닛 추가) ----------

const VERT = `#version 300 es
in vec2 position;
out vec2 vUv;
void main() {
  vUv = position * 0.5 + 0.5;
  gl_Position = vec4(position, 0.0, 1.0);
}`;

const FRAG = `#version 300 es
precision highp float;

in vec2 vUv;
out vec4 outColor;

uniform float uTime;
uniform vec4  uAudioAvg;   // OUTPUT voice (agent): low, mid, high, all
uniform vec4  uAudioInAvg; // INPUT voice (user mic): low, mid, high, all
uniform vec4  uCumAudio;   // time-integrated audio
uniform sampler2D uTexture;
uniform sampler2D uTexture2; // 크로스페이드 도착 텍스처
uniform float uTexMix;       // 0 = uTexture, 1 = uTexture2
uniform vec2  uResolution;
uniform vec2  uTextureRes;

/* call-state params (smoothly tweened in JS) */
uniform float uStateScale;
uniform float uStateSat;
uniform float uStateDim;
uniform float uConnectPulse;
uniform float uConnectPhase;
uniform float uBloom;

/* motion params — 랩의 프리셋 시스템. JS에서 PULSE 상수로 1회 설정 */
uniform float uIdleDrift;    // bulk translation of the sampling center (the "sway")
uniform float uIdleSwirl;    // constant rotation around the orb center, rad/s
uniform float uIdleBreathe;  // radial breathing of silhouette + sampling scale
uniform float uFlow;         // internal churn speed (fbm warp + simplex displacement)
uniform float uVoiceGain;    // output-voice amplitude on the warp
uniform float uVoiceCurve;   // response curve exponent (>1 = quiet speech moves less)
uniform float uFlowAccel;    // how much sustained speech accelerates the flow phase
uniform float uRings;        // expanding pulse-ring strength
uniform float uRingHi;       // rotating ring-highlight strength
uniform float uMicShrink;    // user voice -> shrink amount

/* waveform SHAPE — not how fast it moves, but what the waves look like */
uniform float uWarpScale;    // fbm wave size: <1 big soft billows, >1 fine wrinkles
uniform float uDetailScale;  // simplex detail density, same idea one octave up
uniform float uRipple;       // concentric waves radiating from the center (sonar)
uniform float uEdgeWave;     // the silhouette itself undulates (jelly outline)
uniform float uWaveMode;     // wave LANGUAGE: 0 flow(fbm) 2 ocean 3 vortex 4 scope 5 aurora 6 glint
uniform float uWarpMix;      // how much of the base fbm/simplex warp applies (headroom for modes)

const float SPHERE_SCALE = 1.35;
const float SPHERE_POWER = 1.1;
const float NOISE_SPEED  = 0.6;
const float NOISE_AMP    = 0.17;
const float NOISE_SCALE  = 0.65;
const float FBM_SCALE    = 3.25;
const float FBM_POWER    = 2.75;
const float FBM_AMP      = 0.82;
const float FBM_SPEED    = 4.5;
const float RING_OPACITY = 0.25;
const float EXPOSURE     = 0.04;
const float GRAIN_OPACITY = 0.05;

vec2 getCoverUv(vec2 uv, vec2 containerRes, vec2 textureRes) {
  float ca = containerRes.x / containerRes.y;
  float ta = textureRes.x / textureRes.y;
  vec2 scale = vec2(1.0);
  if (ca > ta) scale.y = ta / ca; else scale.x = ca / ta;
  return (uv - 0.5) * scale + 0.5;
}

vec4 permute(vec4 x) { return mod(((x*34.0)+1.0)*x, 289.0); }
vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }
float snoise(vec3 v) {
  const vec2 C = vec2(1.0/6.0, 1.0/3.0);
  const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);
  vec3 i = floor(v + dot(v, C.yyy));
  vec3 x0 = v - i + dot(i, C.xxx);
  vec3 g = step(x0.yzx, x0.xyz);
  vec3 l = 1.0 - g;
  vec3 i1 = min(g.xyz, l.zxy);
  vec3 i2 = max(g.xyz, l.zxy);
  vec3 x1 = x0 - i1 + 1.0 * C.xxx;
  vec3 x2 = x0 - i2 + 2.0 * C.xxx;
  vec3 x3 = x0 - 1.0 + 3.0 * C.xxx;
  i = mod(i, 289.0);
  vec4 p = permute(permute(permute(
      i.z + vec4(0.0, i1.z, i2.z, 1.0))
      + i.y + vec4(0.0, i1.y, i2.y, 1.0))
      + i.x + vec4(0.0, i1.x, i2.x, 1.0));
  float n_ = 1.0/7.0;
  vec3 ns = n_ * D.wyz - D.xzx;
  vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
  vec4 x_ = floor(j * ns.z);
  vec4 y_ = floor(j - 7.0 * x_);
  vec4 x = x_ * ns.x + ns.yyyy;
  vec4 y = y_ * ns.x + ns.yyyy;
  vec4 h = 1.0 - abs(x) - abs(y);
  vec4 b0 = vec4(x.xy, y.xy);
  vec4 b1 = vec4(x.zw, y.zw);
  vec4 s0 = floor(b0) * 2.0 + 1.0;
  vec4 s1 = floor(b1) * 2.0 + 1.0;
  vec4 sh = -step(h, vec4(0.0));
  vec4 a0 = b0.xzyw + s0.xzyw * sh.xxyy;
  vec4 a1 = b1.xzyw + s1.xzyw * sh.zzww;
  vec3 p0 = vec3(a0.xy, h.x);
  vec3 p1 = vec3(a0.zw, h.y);
  vec3 p2 = vec3(a1.xy, h.z);
  vec3 p3 = vec3(a1.zw, h.w);
  vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2,p2), dot(p3,p3)));
  p0 *= norm.x; p1 *= norm.y; p2 *= norm.z; p3 *= norm.w;
  vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
  m = m * m;
  return 42.0 * dot(m*m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));
}

float random(in vec2 st) {
  return fract(sin(dot(st.xy, vec2(12.9898, 78.233))) * 43758.5453123);
}
vec2 hash2(vec2 p) {
  p = vec2(dot(p, vec2(2127.1, 81.17)), dot(p, vec2(1269.5, 283.37)));
  return fract(sin(p) * 43758.5453);
}
float filmGrainNoise(in vec2 uv) { return length(hash2(uv)); }
float vnoise(in vec2 st) {
  vec2 i = floor(st);
  vec2 f = fract(st);
  float a = random(i);
  float b = random(i + vec2(1.0, 0.0));
  float c = random(i + vec2(0.0, 1.0));
  float d = random(i + vec2(1.0, 1.0));
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(a, b, u.x) + (c - a) * u.y * (1.0 - u.x) + (d - b) * u.x * u.y;
}
float fbm(in vec2 st) {
  float v = 0.0;
  float a = 0.5;
  vec2 shift = vec2(100.0);
  mat2 rot = mat2(cos(0.5), sin(0.5), -sin(0.5), cos(0.5));
  for (int i = 0; i < 4; ++i) {
    v += a * vnoise(st);
    st = rot * st * 2.0 + shift;
    a *= 0.5;
  }
  return v;
}

vec3 czm_saturation(vec3 rgb, float adjustment) {
  const vec3 W = vec3(0.2125, 0.7154, 0.0721);
  vec3 intensity = vec3(dot(rgb, W));
  return mix(intensity, rgb, adjustment);
}

void main() {
  // user voice: rhythmic in-out breathing, never a frozen shrink —
  // base shrink follows the voice envelope, plus a ~1.15Hz oscillation
  float inL = pow(clamp(uAudioInAvg.x * 1.05, 0.0, 1.0), 3.0); // cubed: quiet input barely moves it
  float inBreathe = sin(uTime * 6.2832 * 1.15) * 0.5 + 0.5;
  float circleSize = 1.0 - inL * (0.10 + 0.07 * inBreathe) * uMicShrink;
  circleSize *= uStateScale;
  // idle breathing: slow silhouette expansion/contraction, ~4.5s resting-breath period
  circleSize *= 1.0 + 0.035 * uIdleBreathe * sin(uTime * 6.2832 / 4.5);
  // connecting: breathe in sync with the ringback cadence (3s period)
  circleSize *= 1.0 + 0.028 * sin(uConnectPhase * 6.2832 / 3.0) * uConnectPulse;
  /* output-voice energy, shaped by the lab curve: this is what drives every
     "reacting to the AI voice" term below, so gain/curve tune them together */
  float vAll = pow(clamp(uAudioAvg.w, 0.0, 1.0), uVoiceCurve) * uVoiceGain;
  float vMid = pow(clamp(uAudioAvg.y, 0.0, 1.0), uVoiceCurve) * uVoiceGain;
  float vHigh = pow(clamp(uAudioAvg.z, 0.0, 1.0), uVoiceCurve) * uVoiceGain;

  /* edge wave: angular noise bends the outline itself — jelly-like, voice widens it */
  if (uEdgeWave > 0.001) {
    vec2 eDir = vUv - 0.5;
    float eAng = atan(eDir.y, eDir.x);
    float ew = snoise(vec3(cos(eAng) * 1.3, sin(eAng) * 1.3, uTime * 0.45));
    circleSize *= 1.0 + ew * uEdgeWave * (0.030 + vAll * 0.09);
  }

  vec2 uv = vUv;

  /* ---- fake-3D sphere mapping ---- */
  vec2 uvDot = (uv - 0.5) * 2.0;
  float d = sqrt(1.0 - clamp(dot(uvDot, uvDot), 0.0, 1.0));
  d = pow(d, SPHERE_POWER);
  vec3 normals = vec3(uvDot, d);
  vec2 uvScale = vec2(1.0 / SPHERE_SCALE);
  uvDot /= (vec2(d) + 1.0) * uvScale;
  uvDot = (uvDot + 1.0) * 0.5;
  uv = uvDot;

  /* ---- idle motion: drift / swirl / breathe ---- */
  uv += vec2(sin(uTime * 0.55), cos(uTime * 0.42 + 0.8)) * 0.03 * uIdleDrift;
  uv += vec2(sin(uTime * 0.16 + 2.1), cos(uTime * 0.21)) * 0.022 * uIdleDrift;
  float sw = uTime * uIdleSwirl;
  uv = 0.5 + mat2(cos(sw), -sin(sw), sin(sw), cos(sw)) * (uv - 0.5);
  uv = 0.5 + (uv - 0.5) * (1.0 - 0.06 * uIdleBreathe * sin(uTime * 6.2832 / 4.5));
  /* modeBright: wave modes DRAW their pattern (luminance), not just displace.
     modeTint carries colored light (prismatic fringes) for modes that need it. */
  float modeBright = 0.0;
  vec3 modeTint = vec3(0.0);

  /* ripple = concentric wave trains radiating outward; speech raises the swell */
  if (uRipple > 0.001) {
    vec2 cDir = vUv - 0.5;
    float cDist = length(cDir);
    float rippleWave = sin(cDist * 26.0 - uTime * 2.4);
    uv += (cDir / max(cDist, 1e-4)) * rippleWave * uRipple * (0.014 + vAll * 0.05);
    modeBright += smoothstep(0.45, 1.0, rippleWave) * uRipple
                * (0.05 + vAll * 0.12) * smoothstep(0.55, 0.15, cDist);
  }

  /* ---- wave language modes (랩 원본 그대로 — 프로덕션은 6 glint) ---- */
  int wm = int(uWaveMode + 0.5);
  if (wm == 2) {
    /* OCEAN: horizontal waves rolling sideways */
    float ph = vUv.x * 6.5 + vUv.y * 1.2 - uTime * (1.4 * uFlow) + uCumAudio.x * 0.25 * uFlowAccel;
    float roll = sin(ph) * 0.6 + sin(ph * 0.53 + 1.7) * 0.4;
    uv.y += roll * (0.038 + vAll * 0.10);
    uv.x += sin(ph * 0.31 + 0.6) * (0.012 + vAll * 0.03);
    modeBright += smoothstep(0.2, 1.0, roll) * (0.09 + vAll * 0.13);
    modeBright -= smoothstep(0.2, 1.0, -roll) * (0.065 + vAll * 0.06);
  } else if (wm == 3) {
    /* VORTEX: organic rotation — bounded oscillations, never winds into moire */
    vec2 wob = vec2(sin(uTime * 0.13), cos(uTime * 0.17 + 1.0)) * 0.03;
    vec2 c = uv - 0.5 - wob;
    float dl = length(c);
    float core = 1.0 - smoothstep(0.0, 0.8, dl);
    float spin = (uTime * 0.55
                + 2.6 * sin(uTime * 0.21)
                + 5.4 * sin(uTime * 0.083 + 1.7)
                + 1.1 * sin(uTime * 0.31 + 4.0)) * 0.3 * uFlow
                + uCumAudio.x * 0.15 * uFlowAccel;
    float lead = core * (0.7 * sin(uTime * 0.23) + 0.5 * sin(uTime * 0.11 + 2.0) + vAll * 1.6);
    float twist = spin + lead;
    float cs2 = cos(twist), sn2 = sin(twist);
    uv = 0.5 + wob + mat2(cs2, -sn2, sn2, cs2) * c;
    vec2 sc = vUv - 0.5;
    float sd = length(sc);
    float sa = atan(sc.y, sc.x);
    float spiral = sin(sa * 2.0 + sd * 11.0 + spin * 1.5 + lead * 2.0);
    modeBright += smoothstep(0.55, 1.0, spiral) * (0.045 + vAll * 0.10) * smoothstep(0.55, 0.1, sd);
  } else if (wm == 4) {
    /* SCOPE: a living voice-line across the equator */
    float sx = vUv.x;
    float rimFade = smoothstep(0.50, 0.34, abs(sx - 0.5));
    float energy = 0.5 + 0.5 * sin(sx * 5.3 - uTime * 1.1);
    float wamp = (0.018 + vAll * 0.11 + vMid * 0.05) * rimFade * (0.55 + 0.45 * energy);
    float drift = snoise(vec3(sx * 2.3, uTime * 0.22, 4.7)) * 1.8;
    float w1 = sin(sx * 17.0 - uTime * 2.5 + drift) * 0.55
             + sin(sx * 7.1 - uTime * 1.25) * 0.30
             + sin(sx * 29.0 - uTime * 4.0) * 0.15 * (0.25 + vHigh);
    float w2 = sin(sx * 13.0 - uTime * 2.0 - drift * 0.7 + 1.9) * 0.6
             + sin(sx * 23.0 + uTime * 3.1) * 0.2 * (0.3 + vMid);
    float d1 = abs(vUv.y - (0.5 + w1 * wamp));
    float d2 = abs(vUv.y - (0.5 + w2 * wamp * 0.8));
    float breathe2 = 0.8 + 0.2 * sin(uTime * 0.7);
    float lit = (0.11 + vAll * 0.34) * breathe2 * rimFade;
    modeBright += exp(-d1 * d1 * 6500.0) * lit;
    modeBright += exp(-d1 * d1 * 700.0) * lit * 0.45;
    modeBright += exp(-d2 * d2 * 9000.0) * lit * 0.5;
    modeBright += exp(-d1 * d1 * 90.0) * (0.02 + vAll * 0.07) * rimFade;
    uv.y += w1 * wamp * exp(-pow((vUv.y - 0.5) * 4.0, 2.0)) * 0.9;
  } else if (wm == 5) {
    /* AURORA: diagonal luminous curtains drifting through */
    float diag = (vUv.x + vUv.y) * 4.2 - uTime * (0.8 * uFlow) - uCumAudio.x * 0.2 * uFlowAccel;
    float curtain = sin(diag) * 0.55 + sin(diag * 0.43 + 1.3) * 0.45;
    vec2 dg = vec2(0.7071, -0.7071);
    uv += dg * curtain * (0.020 + vAll * 0.06);
    float veil = 0.75 + 0.25 * snoise(vec3(vUv * 2.2, uTime * 0.3));
    modeBright += smoothstep(0.15, 1.0, curtain) * veil * (0.06 + vAll * 0.13);
    modeBright -= smoothstep(0.5, 1.0, -curtain) * 0.025;
  } else if (wm == 6) {
    /* GLINT: ONE light source, wandering continuously — no cycle resets,
       no fixed region. Each ray lives its own life; texture stays almost still. */
    vec2 src = vec2(0.5) + vec2(
        sin(uTime * 0.024) * 0.55 + sin(uTime * 0.058 + 2.0) * 0.45,
        sin(uTime * 0.031 + 1.3) * 0.55 + sin(uTime * 0.047 + 4.1) * 0.45) * 0.025;
    vec2 rel = vUv - src;
    float rd = length(rel);
    /* NO rotation anywhere: beam GEOMETRY is anchored — only intensity and
       length breathe, in place. */
    float a = atan(rel.y, rel.x);
    vec2 cd = vec2(cos(a), sin(a));                            // seam-free angular coords
    float tspd = 1.0 + vAll * 0.8;                             // sound quickens the shimmer
    /* two ray families: a few THICK soft beams + sparse THIN crisp filaments */
    float bShape = snoise(vec3(cd * 0.8, 3.7 + uTime * 0.018));
    float bEnergy = snoise(vec3(cd * 0.9 + 20.0, uTime * 0.05 * tspd));
    float broad = clamp(0.45 + 0.40 * bShape + 0.30 * bEnergy, 0.0, 1.0);
    broad = broad * broad * (3.0 - 2.0 * broad);
    float tShape = snoise(vec3(cd * 4.5 + 11.0, 7.3 + uTime * 0.022));
    float tEnergy = snoise(vec3(cd * 2.0 + 33.0, uTime * 0.06 * tspd));
    float thin = clamp(tShape * 0.7 + tEnergy * 0.5 - 0.35, 0.0, 1.0);
    thin = pow(max(thin, 1e-4), 2.2) * 2.2;
    float rays = broad;
    /* directional fan, not a full wheel. max() guards: pow(negative, 1.5) is NaN */
    float lobe = 0.35 + 0.65 * pow(max(0.5 + 0.5 * cos(a - (uTime * 0.012 + 0.8 * sin(uTime * 0.013))), 0.0), 1.5);
    /* per-ray reach: a bright ray is a long ray; speech extends them all */
    float ext = 0.62 + 0.38 * snoise(vec3(cd * 1.3 + 40.0, uTime * 0.05 * tspd));
    float reach = (0.16 + 0.34 * rays) * ext * (0.85 + 0.15 * sin(uTime * 0.08)) + vAll * 0.40;
    float reachT = reach * 1.45 + 0.05;
    float fall = exp(-rd * rd / max(reach * reach, 1e-4));
    float fallT = exp(-rd * rd / max(reachT * reachT, 1e-4));
    /* luminous fronts stream from the source toward the rim */
    float sph = rd * 7.5 - uTime * 0.4 - uCumAudio.z * 0.3 * uFlowAccel;
    float stream = 0.65 + 0.35 * (0.6 * sin(sph) + 0.4 * sin(sph * 1.7 + 1.0));
    uv += (rel / max(rd, 1e-3)) * sin(sph) * (0.011 + vAll * 0.032) * fall;
    float vLow = pow(clamp(uAudioAvg.x, 0.0, 1.0), uVoiceCurve) * uVoiceGain;
    /* core stays a small ember, never a flash — the rays are the subject */
    float core = exp(-rd * rd * 240.0) * (0.22 + 0.08 * sin(uTime * 0.08)) * (1.0 + vLow * 0.8);
    float glow = (broad * fall * (1.0 + vAll * 0.8) + thin * fallT * (0.75 + vHigh * 1.3)) * lobe * stream + core;
    glow = glow / (1.0 + glow * 0.45);  // soft-knee: peaks compress instead of blowing out white
    float frp = a * 3.0 + rd * 7.0;
    vec3 irid = vec3(0.5 + 0.5 * sin(frp), 0.5 + 0.5 * sin(frp + 2.09), 0.5 + 0.5 * sin(frp + 4.19));
    modeTint += mix(vec3(1.0), irid, 0.10) * glow * (0.32 + vAll * 0.45);
  }

  /* ---- FBM domain warp (audio speeds up flow via uCumAudio) ---- */
  float fbmTime2 = uTime * (FBM_SPEED * 0.5) * uFlow + uCumAudio.x * 0.25 * uFlowAccel;
  vec2 fbmUv = uv * FBM_SCALE * uWarpScale;
  vec2 q;
  q.x = fbm(fbmUv);
  q.y = fbm(fbmUv + vec2(1.0));
  vec2 r;
  r.x = fbm(fbmUv + q + vec2(91.3, 0.55) + 0.55 * fbmTime2);
  r.y = fbm(fbmUv + q - vec2(45.33, 1.2) + 0.46 * fbmTime2);
  float f = fbm(fbmUv + r);
  float ffbm = mix(0.8, 0.66, clamp(f * f * FBM_POWER, 0.0, 1.0));
  ffbm = mix(ffbm, 0.0, clamp(length(q), 0.0, 1.0));
  ffbm = mix(ffbm, 1.0, clamp(abs(r.x), 0.0, 1.0));

  /* ---- simplex displacement (audio boosts amplitude) ---- */
  float nt1 = uTime * NOISE_SPEED * 0.5 * uFlow + uCumAudio.z * 0.1 * uFlowAccel;
  float nt2 = uTime * NOISE_SPEED * uFlow;
  vec2 noiseDisp = vec2(
    snoise(vec3(vUv * NOISE_SCALE * uDetailScale, nt1)),
    snoise(vec3(vUv * NOISE_SCALE * uDetailScale + vec2(54.0), nt2))
  );
  noiseDisp *= 1.0 + vHigh * 0.25 + vAll * 1.2;

  /* ---- UV shift + texture sample (크로스페이드: 두 유닛을 같은 UV로 샘플) ---- */
  float idleEbb = 1.0 - 0.2 * uFlow + 0.2 * uFlow * sin(uTime * 0.55 + 1.3);
  uv += normals.xy * (ffbm - 0.5) * FBM_AMP * (idleEbb + vAll * 0.9) * uWarpMix;
  uv += noiseDisp * NOISE_AMP * uWarpMix;
  vec2 coverUv = getCoverUv(uv, uResolution, uTextureRes);
  vec3 gradient = mix(texture(uTexture, coverUv).rgb, texture(uTexture2, coverUv).rgb, uTexMix);

  /* ---- fake-3D sphere lighting (depth cue: shade + rim + specular) ---- */
  vec3 N = normalize(vec3(normals.xy, max(normals.z, 0.001)));
  vec3 L = normalize(vec3(-0.45, 0.55, 0.72));
  float diff = clamp(dot(N, L), 0.0, 1.0);
  float shade = mix(0.94, 1.03, diff);
  shade *= mix(0.91, 1.0, smoothstep(0.0, 0.6, normals.z));
  gradient = czm_saturation(gradient * shade, 1.1);
  vec3 R = reflect(vec3(0.0, 0.0, -1.0), N);
  float spec = pow(clamp(dot(R, L), 0.0, 1.0), 14.0);
  gradient += spec * 0.03;

  /* ---- expanding pulse rings (the "sound wave" effect) ---- */
  vec2 circleUv = vUv - 0.5;
  float dist = length(circleUv);
  float pTime = uTime * 0.2 * uFlow + uCumAudio.z * 0.4 * uFlowAccel;
  float pDist = mod(dist * 2.0 - pTime, 1.0);
  float pulse = smoothstep(0.0, 0.75, pDist) - smoothstep(0.75, 1.0, pDist);
  pulse = pulse * pulse * vMid * uRings;

  /* ---- rotating ring highlight (only visible with audio) ---- */
  float ringNoiseScale = 0.65 + uAudioAvg.x * 0.4;
  vec2 ringUv = circleUv * 2.0 / max(circleSize, 1e-3);
  ringUv *= 0.75;
  float ang = atan(ringUv.y, ringUv.x);
  float len = length(ringUv);
  float ringTime = (-uTime * 0.5 * uFlow) - uCumAudio.w * 0.2 * uFlowAccel;
  ringUv.x += 1.0 + vMid * 1.5 - uAudioInAvg.y * 2.5;
  float n0 = snoise(vec3(ringUv * ringNoiseScale, ringTime * 0.5)) * 0.5 + 0.5;
  float cl = cos(ang + ringTime * 2.0) * 0.5 + 0.5;
  float v2 = smoothstep(1.0, mix(0.25, 1.0, n0 * 0.5), len);
  float v3 = pow(smoothstep(0.25, mix(0.25, 1.0, n0 * 0.75), len), 2.0);
  cl = cl * v2 * v3;
  cl = clamp(pow(cl * min(vAll * 4.0 + uAudioInAvg.x * 4.0, 1.0), 3.0), 0.0, 1.0);

  /* ---- compositing + grade ---- */
  vec3 color = gradient + vec3(cl) * RING_OPACITY * uRingHi + vec3(pulse) * 0.18
             + modeBright * (vec3(0.72) + gradient * 0.5)
             + modeTint;
  float cph = fract(uConnectPhase / 3.0);
  float cring = smoothstep(0.10, 0.0, abs(dist * 2.0 - cph * 1.5))
              * max(1.0 - cph * 1.4, 0.0) * uConnectPulse;
  color += vec3(cring) * 0.28;

  color = mix(color, color * filmGrainNoise(vUv), GRAIN_OPACITY);
  color = czm_saturation(color, uStateSat);
  color *= exp2(EXPOSURE) * uStateDim;
  color += uBloom * 0.32;

  float feather = 0.028;
  float m = 1.0 - smoothstep(circleSize - feather, circleSize, dist * 2.0);
  float mask = m * m * (3.0 - 2.0 * m);

  float alpha = mask;
  outColor = vec4(clamp(color, 0.0, 1.0) * alpha, alpha);
}`;

// ---------- 모션 프리셋 — Pulse (디자인 확정) ----------
// orb-demo-vert.vercel.app MOTION_PRESETS.pulse + 미지정 키는 MOTION_DEFS 기본값.
// wave mode 없음(0 = 기본 fbm flow 언어) — 차이는 반응 곡선: 휴지 시 거의 정지,
// 발화 시 강하게 스냅(uVoiceGain 2.2, uVoiceCurve 2.0, uRings/uRingHi 증폭).
// attack/release는 JS 엔벨로프 쪽(DEFAULT_ORB_TUNING)에 반영되어 있다.
const PULSE = {
  uIdleDrift: 0.03,
  uIdleSwirl: 0,
  uIdleBreathe: 0.4,
  uFlow: 0.25,
  uWarpScale: 1,
  uDetailScale: 1,
  uRipple: 0,
  uEdgeWave: 0,
  uWarpMix: 1,
  uWaveMode: 0,
  uVoiceGain: 2.2,
  uVoiceCurve: 2.0,
  uFlowAccel: 1.8,
  uRings: 1.3,
  uRingHi: 1.4,
  uMicShrink: 1.2,
} as const;

// ---------- 오디오 엔진 (원본 수치 그대로, 사운드 없음) ----------

const BANDS_HZ = { low: [85, 300], mid: [300, 2500], high: [2500, 8000] } as const;
// attack/release 스무딩 계수는 런타임 튜닝(DEFAULT_ORB_TUNING)으로 이동
// 마이크: 방 소음 차단용 임계값 (원본). 출력(TTS): 스트림 레벨이 낮아 더 낮은 임계값 —
// 침묵 구간의 WebRTC 잔류 노이즈만 확실히 걸러 발화 종료 시 오브가 차분해지게 한다.
const MIC_GATE = { open: 0.055, close: 0.03 } as const;
const TTS_GATE = { open: 0.03, close: 0.018 } as const;
// 정규화 증폭 상한 — 게이트 경계 소음이 풀스케일로 튀는 것을 방지
const NORMALIZE_MAX_GAIN = 10;
const MOTION =
  typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches ? 0.35 : 1.0;

type Vec4 = [number, number, number, number]
const BANDS = [0, 1, 2, 3] as const

type Channel = {
  analyser: AnalyserNode | null;
  data: Uint8Array<ArrayBuffer> | null;
  gate: Readonly<{ open: number; close: number }> | null;
  normalized: boolean;
  gateOn: boolean;
  peak: number;
  avg: Vec4;
};

const makeChannel = (): Channel => ({
  analyser: null,
  data: null,
  gate: null,
  normalized: false,
  gateOn: false,
  peak: 0.05,
  avg: [0, 0, 0, 0],
});

const attachAnalyser = (
  ch: Channel,
  analyser: AnalyserNode,
  opts: Readonly<{ gate: Readonly<{ open: number; close: number }> | null; normalized: boolean }>,
) => {
  ch.analyser = analyser;
  ch.data = new Uint8Array(new ArrayBuffer(analyser.frequencyBinCount));
  ch.gate = opts.gate;
  ch.normalized = opts.normalized;
  ch.gateOn = false;
  ch.peak = 0.05;
};

const detachAnalyser = (ch: Channel) => {
  // avg는 그대로 두면 release 곡선으로 자연 페이드아웃
  ch.analyser = null;
  ch.data = null;
  ch.gateOn = false;
  ch.peak = 0.05;
};

const bandRange = (analyser: AnalyserNode, loHz: number, hiHz: number): [number, number] => {
  const nyquist = analyser.context.sampleRate / 2;
  const n = analyser.frequencyBinCount;
  return [Math.max(0, Math.round((loHz / nyquist) * n)), Math.min(n - 1, Math.round((hiHz / nyquist) * n))];
};

const bandAvg = (data: Uint8Array<ArrayBuffer>, lo: number, hi: number): number => {
  let sum = 0;
  for (let i = lo; i <= hi; i += 1) sum += data[i] ?? 0;
  return sum / ((hi - lo + 1) * 255);
};

function updateChannel(ch: Channel, dt: number, attack: number, release: number) {
  if (!ch.analyser || !ch.data) {
    for (const i of BANDS) ch.avg[i] += (0 - ch.avg[i]) * (1 - Math.pow(1 - release, dt * 60));
    return;
  }
  ch.analyser.getByteFrequencyData(ch.data);
  const [l0, l1] = bandRange(ch.analyser, BANDS_HZ.low[0], BANDS_HZ.low[1]);
  const [m0, m1] = bandRange(ch.analyser, BANDS_HZ.mid[0], BANDS_HZ.mid[1]);
  const [h0, h1] = bandRange(ch.analyser, BANDS_HZ.high[0], BANDS_HZ.high[1]);
  const target: Vec4 = [bandAvg(ch.data, l0, l1), bandAvg(ch.data, m0, m1), bandAvg(ch.data, h0, h1), 0];
  target[3] = (target[0] + target[1] + target[2]) / 3;

  if (ch.gate) {
    // 히스테리시스 노이즈 게이트: 소음(방 소음/WebRTC 잔류 노이즈)은 오브를 흔들지 않는다
    if (ch.gateOn) {
      if (target[3] < ch.gate.close) ch.gateOn = false;
    } else if (target[3] > ch.gate.open) ch.gateOn = true;
    if (!ch.gateOn) target[0] = target[1] = target[2] = target[3] = 0;
  }
  if (ch.normalized) {
    // 롤링 피크 자동 정규화: 소스 음량(마이크 거리, TTS 스트림 레벨)과 무관한 반응.
    // 게이트 통과 후에만 적용되고, 증폭은 NORMALIZE_MAX_GAIN으로 상한.
    ch.peak = Math.max(ch.peak * Math.exp(-dt / 4), target[3], 0.05);
    const scale = Math.min(0.7 / ch.peak, NORMALIZE_MAX_GAIN);
    for (const i of BANDS) target[i] = Math.min(target[i] * scale, 1);
  }

  for (const i of BANDS) {
    const t = target[i] * MOTION;
    const k = t > ch.avg[i] ? attack : release; // 비대칭 스무딩
    ch.avg[i] += (t - ch.avg[i]) * (1 - Math.pow(1 - k, dt * 60)); // 프레임레이트 독립
  }
}

// ---------- 콜 상태 비주얼 (원본 STATE_TARGETS, 사운드 제외) ----------

const STATE_TARGETS: Record<OrbState, { motion: number; sat: number; dim: number; scale: number; pulse: number }> = {
  idle: { motion: 1.0, sat: 1.0, dim: 1.0, scale: 1.0, pulse: 0 },
  connecting: { motion: 0.5, sat: 0.78, dim: 0.97, scale: 0.955, pulse: 1 },
  connected: { motion: 1.45, sat: 1.06, dim: 1.02, scale: 1.0, pulse: 0 },
  speaking: { motion: 1.6, sat: 1.06, dim: 1.0, scale: 1.0, pulse: 0 },
  ended: { motion: 0.12, sat: 0.22, dim: 0.92, scale: 0.9, pulse: 0 },
};

// ---------- WebGL 엔진 ----------

/**
 * 오디오 → 시각 반응 튜닝 (디버거에서 실시간 조절, 디자이너 피드백용).
 * input/output: 유저(수축)/에이전트(파동) 민감도 배수, attack: 반응 시작 속도,
 * releaseIn/releaseOut: 각 채널이 잦아드는 속도.
 */
export type OrbTuning = Readonly<{
  input: number;
  output: number;
  attack: number;
  releaseIn: number;
  releaseOut: number;
}>;

// Pulse 프리셋 스펙 (orb-demo-vert.vercel.app — attack .6 / release .08).
// 파동 증폭은 셰이더의 uVoiceGain(2.2)이 담당 — JS에서 또 곱하면 이중 증폭이므로
// output 1.0. releaseIn(마이크 수축 호흡)은 랩 원본의 0.22 유지.
export const DEFAULT_ORB_TUNING: OrbTuning = {
  input: 1.0,
  output: 1.0,
  attack: 0.6,
  releaseIn: 0.22,
  releaseOut: 0.08,
};

export type OrbEngine = Readonly<{
  setTexture: (url: string) => void;
  /** 텍스처들을 미리 디코드·GPU 업로드해 상주시킨다 — 이후 setTexture는 바인딩 스위치만 */
  preload: (urls: readonly string[]) => void;
  setState: (next: OrbState) => void;
  setTuning: (next: Partial<OrbTuning>) => void;
  attachOutput: (a: AnalyserNode | null) => void;
  attachInput: (a: AnalyserNode | null) => void;
  /** releaseContext=false: 컨텍스트 복구 후 재생성 경로 — 방금 살아난 컨텍스트를 반납하면 안 된다 */
  destroy: (releaseContext?: boolean) => void;
}>;

const UNIFORMS = [
  'uTime', 'uAudioAvg', 'uAudioInAvg', 'uCumAudio', 'uTexture', 'uTexture2', 'uTexMix',
  'uResolution', 'uTextureRes',
  'uStateScale', 'uStateSat', 'uStateDim', 'uConnectPulse', 'uConnectPhase', 'uBloom',
  'uIdleDrift', 'uIdleSwirl', 'uIdleBreathe', 'uFlow', 'uVoiceGain', 'uVoiceCurve',
  'uFlowAccel', 'uRings', 'uRingHi', 'uMicShrink',
  'uWarpScale', 'uDetailScale', 'uRipple', 'uEdgeWave', 'uWaveMode', 'uWarpMix',
] as const;

export function createOrbEngine(canvas: HTMLCanvasElement): OrbEngine | null {
  const gl = canvas.getContext('webgl2', { alpha: true, premultipliedAlpha: true, antialias: false });
  if (!gl) return null;

  const compile = (type: number, src: string) => {
    const s = gl.createShader(type)!;
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) console.error(gl.getShaderInfoLog(s));
    return s;
  };
  const prog = gl.createProgram()!;
  gl.attachShader(prog, compile(gl.VERTEX_SHADER, VERT));
  gl.attachShader(prog, compile(gl.FRAGMENT_SHADER, FRAG));
  gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) console.error(gl.getProgramInfoLog(prog));
  gl.useProgram(prog);

  const buf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
  const locPos = gl.getAttribLocation(prog, 'position');
  gl.enableVertexAttribArray(locPos);
  gl.vertexAttribPointer(locPos, 2, gl.FLOAT, false, 0, 0);

  gl.enable(gl.BLEND);
  gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

  type UniformName = (typeof UNIFORMS)[number]
  const U = Object.fromEntries(
    UNIFORMS.map((n) => [n, gl.getUniformLocation(prog, n)]),
  ) as { [K in UniformName]: WebGLUniformLocation | null }

  // 모션 프리셋(Pulse) — 상수라 1회 업로드
  for (const [key, value] of Object.entries(PULSE)) {
    gl.uniform1f(U[key as UniformName], value);
  }

  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const resize = () => {
    const rect = canvas.parentElement?.getBoundingClientRect();
    const px = Math.min(Math.max(1, Math.round((rect?.width ?? 230) * dpr)), 720); // 모바일 GPU 캡
    if (canvas.width !== px) {
      canvas.width = px;
      canvas.height = px;
      gl.viewport(0, 0, px, px);
      gl.uniform2f(U.uResolution, px, px);
    }
  };
  resize();
  const ro = new ResizeObserver(resize);
  if (canvas.parentElement) ro.observe(canvas.parentElement);

  // 텍스처 — URL별로 GPU에 상주시키는 캐시. 탭 전환은 바인딩 스위치뿐이라
  // 프레임 드랍이 없다. 로드/디코드/업로드는 preload 시점(스플래시 아래)에 끝낸다.
  const makeTexture = (): WebGLTexture => {
    const t = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, t);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    return t;
  };

  gl.activeTexture(gl.TEXTURE0);
  gl.uniform1i(U.uTexture, 0);
  gl.uniform1i(U.uTexture2, 1);
  gl.uniform1f(U.uTexMix, 0);
  // 이미지 로드 전에는 안 보이도록 투명 픽셀
  const placeholderTex = makeTexture();
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([0, 0, 0, 0]));
  gl.uniform2f(U.uTextureRes, 1, 1);

  const textureCache = new Map<string, WebGLTexture>();
  const pendingUrls = new Set<string>();
  // 텍스처 상태: 유닛 0 = base(출발), 유닛 1 = fade(도착, 크로스페이드 중에만)
  let wantUrl: string | null = null;
  let baseUrl: string | null = null;
  let fadeUrl: string | null = null;
  let texMix = 0;
  const TEX_FADE_SECS = 0.5;

  // 업로드가 바인딩을 바꾸므로, 항상 base/fade 기준으로 두 유닛을 복구한다.
  // 불변식: 이 함수가 끝나면 활성 유닛은 TEXTURE0.
  const bindUnits = () => {
    const base = baseUrl !== null ? textureCache.get(baseUrl) : undefined;
    const fade = fadeUrl !== null ? textureCache.get(fadeUrl) : undefined;
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, fade ?? base ?? placeholderTex);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, base ?? placeholderTex);
    gl.uniform2f(U.uTextureRes, base ? 1024 : 1, base ? 1024 : 1);
  };

  // 상주 텍스처로 전환 — 최초(플레이스홀더)는 즉시, 이후엔 0.5s 크로스페이드
  const applyTexture = (url: string) => {
    if (url === (fadeUrl ?? baseUrl)) return;
    if (baseUrl === null) {
      baseUrl = url;
    } else {
      if (fadeUrl !== null) baseUrl = fadeUrl; // 진행 중이던 페이드는 그 자리에서 커밋
      fadeUrl = url;
      texMix = 0;
    }
    bindUnits();
  };

  const uploadTexture = (url: string, img: HTMLImageElement) => {
    const c = document.createElement('canvas');
    c.width = c.height = 1024;
    const x = c.getContext('2d')!;
    const zoom = 1.45; // 원형/투명 가장자리 안쪽으로 크롭
    const s = Math.max(1024 / img.width, 1024 / img.height) * zoom;
    const w = img.width * s;
    const h = img.height * s;
    x.drawImage(img, (1024 - w) / 2, (1024 - h) / 2, w, h);
    gl.activeTexture(gl.TEXTURE0);
    const t = makeTexture(); // 생성 직후 바인딩된 상태
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, c);
    textureCache.set(url, t);
    if (wantUrl === url) applyTexture(url);
    else bindUnits();
  };

  const ensureTexture = (url: string) => {
    if (textureCache.has(url) || pendingUrls.has(url)) return;
    pendingUrls.add(url);
    const img = new Image();
    img.src = url;
    // decode()로 디코드를 비동기화 — drawImage의 메인 스레드 동기 디코드를 피한다
    void img
      .decode()
      .catch(() => undefined)
      .then(() => {
        pendingUrls.delete(url);
        if (destroyed || img.naturalWidth === 0) return;
        uploadTexture(url, img);
      });
  };

  // 엔진 상태
  // 원본은 출력을 "마스터링 오디오 raw"로 쓰지만, 우리 출력은 WebRTC TTS 스트림이라
  // 레벨이 낮고 가변적 — 정규화를 켠다. 파동 증폭은 셰이더 uVoiceGain(Glint 1.4)이 담당.
  const chOut = makeChannel(); // 파형·링·흐름 — attack/release는 tune(Glint 스펙)에서
  const chIn = makeChannel(); // 수축 호흡
  let tune: OrbTuning = { ...DEFAULT_ORB_TUNING }; // 디버거에서 실시간 조절
  const cumAudio: Vec4 = [0, 0, 0, 0];
  let callState: OrbState = 'idle';
  let stateTime = 0;
  let bloom = 0;
  const sp = { motion: 1, sat: 1, dim: 1, scale: 1, pulse: 0 };
  let time = 0;
  let last = performance.now();
  let raf = 0;
  let destroyed = false;

  const frame = (now: number) => {
    if (destroyed) return;
    const dt = Math.min((now - last) / 1000, 0.05);
    last = now;

    const tgt = STATE_TARGETS[callState];
    const kk = 1 - Math.exp(-dt * 4);
    sp.motion += (tgt.motion - sp.motion) * kk;
    sp.sat += (tgt.sat - sp.sat) * kk;
    sp.dim += (tgt.dim - sp.dim) * kk;
    sp.scale += (tgt.scale - sp.scale) * kk;
    sp.pulse += (tgt.pulse - sp.pulse) * kk;
    bloom *= Math.exp(-dt * 2.4);
    stateTime += dt;
    time += dt * sp.motion;

    updateChannel(chOut, dt, tune.attack, tune.releaseOut);
    updateChannel(chIn, dt, tune.attack, tune.releaseIn);
    const outGain = (i: (typeof BANDS)[number]) => Math.min(chOut.avg[i] * tune.output, 1);
    const inGain = (i: (typeof BANDS)[number]) => Math.min(chIn.avg[i] * tune.input, 1);
    for (const i of BANDS) cumAudio[i] += outGain(i) * dt * 8.0;

    // 텍스처 크로스페이드 진행 — 완료 시 도착 텍스처를 base로 승격
    if (fadeUrl !== null) {
      texMix = Math.min(1, texMix + dt / TEX_FADE_SECS);
      if (texMix >= 1) {
        baseUrl = fadeUrl;
        fadeUrl = null;
        texMix = 0;
        bindUnits();
      }
    }
    gl.uniform1f(U.uTexMix, fadeUrl !== null ? texMix : 0);

    gl.uniform1f(U.uTime, time);
    gl.uniform1f(U.uStateScale, sp.scale);
    gl.uniform1f(U.uStateSat, sp.sat);
    gl.uniform1f(U.uStateDim, sp.dim);
    gl.uniform1f(U.uConnectPulse, sp.pulse);
    gl.uniform1f(U.uConnectPhase, stateTime);
    gl.uniform1f(U.uBloom, bloom);
    gl.uniform4f(U.uAudioAvg, outGain(0), outGain(1), outGain(2), outGain(3));
    gl.uniform4f(U.uAudioInAvg, inGain(0), inGain(1), inGain(2), inGain(3));
    gl.uniform4f(U.uCumAudio, cumAudio[0], cumAudio[1], cumAudio[2], cumAudio[3]);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

    raf = requestAnimationFrame(frame);
  };
  raf = requestAnimationFrame(frame);

  return {
    setTexture: (url: string) => {
      wantUrl = url;
      // 상주 텍스처면 크로스페이드 시작. 아니면 이전 텍스처를 유지한 채(깜빡임 방지)
      // 로드/업로드 완료 시점의 uploadTexture가 마저 전환한다.
      if (textureCache.has(url)) applyTexture(url);
      else ensureTexture(url);
    },
    preload: (urls) => urls.forEach(ensureTexture),
    setState: (next: OrbState) => {
      if (next === callState) return;
      // 연결 확정 플래시 — 통화 성립 순간 한 번만. speaking→connected(발화 종료)
      // 전이에서 재발화하지 않는다 (디자인 피드백).
      if (next === 'connected' && (callState === 'connecting' || callState === 'idle')) bloom = 1;
      callState = next;
      stateTime = 0;
    },
    setTuning: (next) => {
      tune = { ...tune, ...next };
    },
    attachOutput: (a) =>
      a ? attachAnalyser(chOut, a, { gate: TTS_GATE, normalized: true }) : detachAnalyser(chOut),
    attachInput: (a) =>
      a ? attachAnalyser(chIn, a, { gate: MIC_GATE, normalized: true }) : detachAnalyser(chIn),
    destroy: (releaseContext = true) => {
      destroyed = true;
      cancelAnimationFrame(raf);
      ro.disconnect();
      if (releaseContext) gl.getExtension('WEBGL_lose_context')?.loseContext();
    },
  };
}
